'use client';
import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import { loadLazyImage } from '@/utils';
import { ScrollerMotion } from 'scroller-motion';
import tailwindConfig from '@/tailwind.config';
import { useParams } from 'next/navigation';
import { scrollIntoView } from '@/utils';

export interface IPageContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const PageContainer = ({ className, ...props }: IPageContainerProps) => {
  const lazyTimer = useRef<ReturnType<typeof setTimeout>>(null);

  const [isDesktop, setIsDesktop] = useState(false);
  const navigationParams = useParams();

  useEffect(() => {
    const _setIsDesktop = () => {
      setIsDesktop(window.innerWidth >= parseInt(tailwindConfig.theme.screens.lg, 10));
    };

    startToLoadLazyImages();
    watchDisableScroll();
    _setIsDesktop();

    window.addEventListener('resize', _setIsDesktop);
    return () => window.removeEventListener('resize', _setIsDesktop);
  }, []);

  useEffect(() => {
    if (window.location.hash) {
      try {
        const element = document.querySelector(window.location.hash);
        if (element) {
          setTimeout(() => {
            scrollIntoView(element);
          }, 300);
        }
      } catch (err) {
        console.log(err);
      }
    }
  }, [navigationParams]);

  const watchDisableScroll = () => {
    if (document.getElementsByClassName('disable-scroll').length > 0) {
      document.body.classList.add('noscroll');
    } else {
      document.body.classList.remove('noscroll');
    }
    setTimeout(() => {
      watchDisableScroll();
    }, 200);
  };

  const startToLoadLazyImages = () => {
    if (lazyTimer.current) {
      clearTimeout(lazyTimer.current);
    }
    lazyTimer.current = setTimeout(() => {
      loadLazyImagesChunk();
    }, 2000);
  };

  // load lazy images
  const loadLazyImagesChunk = () => {
    const elList = document.querySelectorAll('.lazy-image:not(.lazy-loaded)');
    if (elList && elList.length > 0) {
      let imgEl: any = null;
      for (let i = 0; i < 5; i++) {
        loadLazyImage(elList[i]);
        if (elList[i] && i === 4) {
          const media =
            elList[i].children && elList[i].children.length > 0 ? [...elList[i].children, elList[i]] : [elList[i]];

          imgEl = media.find((cEl) => cEl.tagName.toLowerCase() === 'img');
        }
      }
      if (imgEl) {
        if (imgEl.complete) {
          loadLazyImagesChunk();
        } else {
          imgEl.addEventListener('load', () => {
            loadLazyImagesChunk();
          });
        }
      } else {
        loadLazyImagesChunk();
      }
    } else {
      startToLoadLazyImages();
    }
  };

  return (
    <ScrollerMotion className="scroller" disabled={!isDesktop}>
      <div className={classnames('page-container will-change-transform', className)} {...props} />
    </ScrollerMotion>
  );
};

export default PageContainer;
